import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { useGetNotificationLogsQuery } from "../../store/services/notification-logs";
import {
  NotificationLogItem,
  NotificationLogType,
  NotificationLogsQueryParams,
} from "../../models/notification-logs";
import { PageHeader } from "../../components/ui/PageHeader";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { emptyMessage } from "../../components/ui/EmptyState";
import { DateRangeFilter } from "../../components/Filters/DateRangeFilter";
import { useDebounce } from "../../hooks/useDebounce";
import { formatSupportDateTimeMinutes } from "../../utils/functions/parseInstant";
import {
  PERIOD_PRESETS,
  PeriodPreset,
  endOfDay,
  notificationSenderLabel,
  notificationStatusLabel,
  notificationStatusTone,
  notificationTypeLabel,
  rangeForPreset,
  startOfDay,
  truncate,
} from "./notificationLogPresentation";

const typeOptions: { label: string; value: NotificationLogType | null }[] = [
  { label: "Todas", value: null },
  { label: "Manual", value: "MANUAL" },
  { label: "Automática", value: "AUTOMATIC" },
];

export function PushHistory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const today = useMemo(() => new Date(), []);

  const initialType = searchParams.get("type") as NotificationLogType | null;

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [type, setType] = useState<NotificationLogType | null>(initialType);
  const [preset, setPreset] = useState<PeriodPreset>("last30");
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);

  const debouncedSearch = useDebounce(search, 500);

  const range = useMemo(() => {
    const presetRange = rangeForPreset(preset, today);
    if (presetRange) {
      return presetRange;
    }
    return {
      dateFrom: customFrom ? startOfDay(customFrom) : null,
      dateTo: customTo ? endOfDay(customTo) : null,
    };
  }, [preset, customFrom, customTo, today]);

  const queryParams: NotificationLogsQueryParams = useMemo(
    () => ({
      page,
      limit,
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...(type ? { type } : {}),
      ...(range.dateFrom ? { dateFrom: range.dateFrom.toISOString() } : {}),
      ...(range.dateTo ? { dateTo: range.dateTo.toISOString() } : {}),
    }),
    [page, limit, debouncedSearch, type, range]
  );

  const { data, isLoading, isFetching } = useGetNotificationLogsQuery(queryParams);

  const hasFilters =
    debouncedSearch.trim().length > 0 ||
    type !== null ||
    preset !== "last30" ||
    customFrom !== null ||
    customTo !== null;

  const resetPage = () => setPage(1);

  const handlePage = (event: DataTablePageEvent) => {
    setPage((event.page ?? 0) + 1);
    setLimit(event.rows);
  };

  const applyPreset = (next: PeriodPreset) => {
    setPreset(next);
    if (next !== "custom") {
      setCustomFrom(null);
      setCustomTo(null);
    }
    resetPage();
  };

  return (
    <div className="p-card gap-4 p-3">
      <PageHeader title="Historial de notificaciones" />

      <div className="flex flex-wrap align-items-end gap-3 mb-4 px-3">
        <div className="flex flex-column gap-2">
          <label htmlFor="search" className="text-600 text-sm">
            Buscar por título
          </label>
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              id="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                resetPage();
              }}
              placeholder="Título del mensaje"
            />
          </span>
        </div>

        <div className="flex flex-column gap-2">
          <label htmlFor="type" className="text-600 text-sm">
            Tipo
          </label>
          <Dropdown
            inputId="type"
            value={type}
            options={typeOptions}
            onChange={(event) => {
              setType(event.value);
              resetPage();
            }}
            placeholder="Todas"
          />
        </div>

        <div className="flex flex-column gap-2">
          <span className="text-600 text-sm">Período</span>
          <div className="flex flex-wrap gap-2">
            {PERIOD_PRESETS.map((option) => (
              <Button
                key={option.key}
                type="button"
                label={option.label}
                size="small"
                outlined={preset !== option.key}
                onClick={() => applyPreset(option.key)}
              />
            ))}
          </div>
        </div>

        {preset === "custom" && (
          <DateRangeFilter
            variant="stacked"
            columnClassName="flex flex-column gap-2"
            labelClassName="text-600 text-sm"
            labelFrom="Desde"
            labelTo="Hasta"
            calendarStyle={{ width: "11rem" }}
            dateFrom={customFrom}
            dateTo={customTo}
            onDateFromChange={(date) => {
              setCustomFrom(date);
              resetPage();
            }}
            onDateToChange={(date) => {
              setCustomTo(date);
              resetPage();
            }}
            startDateId="notificationLogsDateFrom"
            endDateId="notificationLogsDateTo"
            maxDateFrom={customTo ?? today}
            maxDateTo={today}
            minDateTo={customFrom ?? undefined}
          />
        )}
      </div>

      <DataTable
        value={data?.items ?? []}
        loading={isLoading || isFetching}
        lazy
        paginator
        first={(page - 1) * limit}
        rows={limit}
        totalRecords={data?.total ?? 0}
        rowsPerPageOptions={[10, 25, 50]}
        onPage={handlePage}
        dataKey="id"
        responsiveLayout="scroll"
        tableStyle={{ minWidth: "70rem" }}
        emptyMessage={emptyMessage("notificaciones", hasFilters)}
        selectionMode="single"
        onRowClick={(event) => navigate(`${(event.data as NotificationLogItem).id}`)}
      >
        <Column
          header="Fecha y hora"
          body={(row: NotificationLogItem) => formatSupportDateTimeMinutes(row.sentAt)}
          style={{ width: "11rem" }}
        />
        <Column field="title" header="Título" style={{ minWidth: "14rem" }} />
        <Column
          header="Mensaje"
          body={(row: NotificationLogItem) => (
            <span title={row.body}>{truncate(row.body)}</span>
          )}
          style={{ minWidth: "18rem" }}
        />
        <Column
          header="Enviada por"
          body={(row: NotificationLogItem) => notificationSenderLabel(row)}
          style={{ width: "14rem" }}
        />
        <Column
          header="Tipo"
          body={(row: NotificationLogItem) => (
            <StatusBadge
              label={notificationTypeLabel(row)}
              tone={row.type === "MANUAL" ? "info" : "pending"}
            />
          )}
          style={{ width: "9rem" }}
        />
        <Column
          header="Destinatarios"
          body={(row: NotificationLogItem) => row.recipientsCount}
          style={{ width: "8rem" }}
        />
        <Column
          header="Resultado"
          body={(row: NotificationLogItem) => (
            <StatusBadge
              label={notificationStatusLabel(row.status)}
              tone={notificationStatusTone(row.status)}
            />
          )}
          style={{ width: "9rem" }}
        />
      </DataTable>
    </div>
  );
}
